import apiClient from '@/utils/axios.js';
import { defineStore } from 'pinia';

/**
 * The cashier's till: whether the feature is on, what they may do, and the
 * session they have open right now.
 *
 * Nothing here is cached in Dexie. A drawer is real money and only the
 * server's answer counts, so the state is re-read at POS mount, after every
 * till action and on every reconnect (`docs/DEXIE_OFFLINE.md`: offline is
 * read-only for server data, and a till operation is a mutation).
 */

/**
 * Whether a cash tender may be taken right now, and why not.
 *
 * Mirrors SaleController::createSale's till preconditions: with a session the
 * payment branch is the session's; without one, a branch that keeps its cash
 * in tills refuses cash from anyone who cannot reach the safe. Exported as a
 * pure function so it can be tested without a Pinia instance.
 *
 * @param {{ enabled: boolean, session: object|null, canManage: boolean }} state
 * @param {boolean|null} branchHasTills  null when unknown (treated as "no tills")
 * @returns {{ allowed: boolean, reason: string|null, paymentBranchId: number|null }}
 */
export function cashTenderRule(state, branchHasTills) {
    if (!state?.enabled) {
        return { allowed: true, reason: null, paymentBranchId: null };
    }

    if (state.session) {
        return {
            allowed: true,
            reason: null,
            paymentBranchId: Number(state.session.branch_id) || null,
        };
    }

    if (branchHasTills && !state.canManage) {
        return {
            allowed: false,
            reason: 'This branch keeps its cash in tills. Open a till before taking cash.',
            paymentBranchId: null,
        };
    }

    return { allowed: true, reason: null, paymentBranchId: null };
}

/**
 * The localStorage key a count pad keeps its half-typed count under: one per
 * purpose (open, close, audit, force) and subject (the till or the session),
 * so a reload mid-count lands back on the same figures (spec §12).
 */
export function countDraftKey(purpose, subjectId) {
    return `inventree_till_count:${purpose}:${subjectId ?? 'new'}`;
}

/** Forget a count draft once the server has the figure. */
export function clearCountDraft(key) {
    if (!key) return;
    try {
        localStorage.removeItem(key);
    } catch {
        // Storage unavailable: nothing to forget.
    }
}

/** Face x quantity summed in integer paisa, so 0.1 + 0.2 never happens. */
export function denominationTotal(counts) {
    let cents = 0;
    for (const [face, qty] of Object.entries(counts || {})) {
        const faceCents = Math.round(Number(face) * 100);
        const quantity = Math.trunc(Number(qty) || 0);
        if (!Number.isFinite(faceCents) || faceCents <= 0 || quantity <= 0) continue;
        cents += faceCents * quantity;
    }
    return cents / 100;
}

const emptyState = () => ({
    enabled: false,
    canUse: false,
    canManage: false,
    session: null,
    denominations: [],
    blindClose: true,
    varianceTolerance: 0,
    pendingCount: 0,
});

export const useTillStore = defineStore('till', {
    state: () => ({
        ...emptyState(),
        loaded: false,
        loading: false,
        /** branchId -> boolean, whether the branch has an active till. */
        branchHasTills: {},
        /** Set by the POS when the server refuses a cash tender for want of a session. */
        panelRequested: false,
    }),

    getters: {
        hasSession: (state) => Boolean(state.session?.id),
        sessionBranchId: (state) => (state.session ? Number(state.session.branch_id) || null : null),
        drawerBalance: (state) => Number(state.session?.drawer_balance ?? 0),
        overThreshold: (state) => Boolean(state.session?.over_threshold),
        sessionLabel: (state) => (state.session ? state.session.till_name || `Till #${state.session.till_id}` : ''),
    },

    actions: {
        /** The switches, the caller's rights and their open session. Never 404s. */
        async fetchState() {
            this.loading = true;
            try {
                const { data } = await apiClient.get('till/state', { skipProgress: true });
                const payload = data?.data ?? {};
                this.enabled = Boolean(payload.addon_enabled);
                this.canUse = Boolean(payload.can_use);
                this.canManage = Boolean(payload.can_manage);
                this.session = payload.session ?? null;
                this.denominations = Array.isArray(payload.denominations) ? payload.denominations.map(Number) : [];
                this.blindClose = payload.blind_close !== false;
                this.varianceTolerance = Number(payload.variance_tolerance ?? 0);
                this.pendingCount = Number(payload.pending_count ?? 0);
                this.loaded = true;
            } catch (error) {
                // Offline or a server fault: keep whatever was known. The
                // server refuses a stale session anyway.
                if (!this.loaded) Object.assign(this, emptyState());
                throw error;
            } finally {
                this.loading = false;
            }
        },

        async refreshSession() {
            if (!this.enabled) return null;
            const { data } = await apiClient.get('till/session/current', { skipProgress: true });
            this.session = data?.data ?? null;
            return this.session;
        },

        /** The branch's tills with occupancy and, for this user, what may be opened. */
        async fetchBranchTills(branchId) {
            const { data } = await apiClient.get(`till/branch/${branchId}`, { skipProgress: true });
            const payload = data?.data ?? {};
            this.branchHasTills = { ...this.branchHasTills, [String(branchId)]: Boolean(payload.has_tills) };
            if (payload.session !== undefined) this.session = payload.session;
            return payload;
        },

        hasTills(branchId) {
            const known = this.branchHasTills[String(branchId)];
            return known === undefined ? null : known;
        },

        /** What the POS needs to know before a cash tender. */
        cashRule(branchId) {
            return cashTenderRule(this, this.hasTills(branchId));
        },

        async openingContext(tillId) {
            const { data } = await apiClient.get(`till/${tillId}/opening-context`, { skipProgress: true });
            return data?.data ?? {};
        },

        async open(tillId, payload) {
            const response = await apiClient.post(`till/${tillId}/open`, payload);
            this.session = response.data?.data ?? null;
            return this.session;
        },

        async closeContext(sessionId) {
            const { data } = await apiClient.get(`till/session/${sessionId}/close-context`, { skipProgress: true });
            return data?.data ?? {};
        },

        async close(sessionId, payload) {
            const response = await apiClient.post(`till/session/${sessionId}/close`, payload);
            const closed = response.data?.data ?? null;
            if (this.session && Number(this.session.id) === Number(sessionId)) this.session = null;
            return closed;
        },

        async sessionReport(sessionId) {
            const { data } = await apiClient.get(`till/session/${sessionId}`, { skipProgress: true });
            return data?.data ?? null;
        },

        async movement(payload) {
            const response = await apiClient.post('till/movement', payload);
            await this.refreshSession().catch(() => null);
            return response.data?.data ?? null;
        },

        async accept(transferId, payload) {
            const response = await apiClient.post(`till/movement/${transferId}/accept`, payload);
            await this.refreshSession().catch(() => null);
            return response.data?.data ?? null;
        },

        async reject(transferId, payload = {}) {
            const response = await apiClient.post(`till/movement/${transferId}/reject`, payload);
            await this.refreshSession().catch(() => null);
            return response.data?.data ?? null;
        },

        async pending() {
            const { data } = await apiClient.get('till/movement/pending', { skipProgress: true });
            const rows = Array.isArray(data?.data) ? data.data : [];
            this.pendingCount = rows.length;
            return rows;
        },

        async floatRequest(payload) {
            const response = await apiClient.post('till/float-request', payload);
            return response.data ?? null;
        },

        requestPanel() {
            this.panelRequested = true;
        },

        clear() {
            Object.assign(this, emptyState(), { loaded: false, loading: false, branchHasTills: {}, panelRequested: false });
        },
    },
});
